import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  X,
  BookOpen,
  Copy,
  Trash2,
  Edit,
  ImagePlus,
  Share2,
  CheckCircle2,
  FileText,
  BarChart3,
  Clock,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { BookCover } from './book-cover';
import { countWords } from '../utils/word-count';
import { useIsMobile } from '@/hooks/use-mobile';
import { Drawer, DrawerContent } from './ui/drawer';
import { Dialog, DialogContent } from './ui/dialog';
import type { Project } from '../types';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function deriveProgress(chapters: { content: string }[]): number {
  const total = chapters.reduce((s, ch) => s + countWords(ch.content), 0);
  return Math.min(100, Math.round((total / 80000) * 100));
}

// ─── types ────────────────────────────────────────────────────────────────────

interface BookDetailDrawerProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
  onEditDetails: (project: Project) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onChangeCover: (id: string) => void;
  onShare: (id: string) => void;
}

// ─── BookDetailDrawer ──────────────────────────────────────────────────────────
// Desktop: Dialog modal centralizado (max-w-md).
// Mobile:  Drawer vaul direction="bottom".
// Cover does a flip-in animation via rAF after mount (no layoutId).

export function BookDetailDrawer({
  project,
  open,
  onClose,
  onEditDetails,
  onDuplicate,
  onDelete,
  onChangeCover,
  onShare,
}: BookDetailDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Flip state — reset to false whenever opened so the flip reruns.
  const [flipped, setFlipped] = useState(false);

  // Keep the last project mounted while the drawer/modal animates OUT, so the
  // exit animation has content to show (parent nulls `project` on close).
  const [shownProject, setShownProject] = useState(project);
  useEffect(() => {
    if (project) setShownProject(project);
  }, [project]);

  useEffect(() => {
    if (!open) {
      setFlipped(false);
      return;
    }
    // Trigger flip after the first paint so the -100 → 0 transition is visible.
    const id = requestAnimationFrame(() => setFlipped(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  const handleOpen = () => {
    if (!project) return;
    onClose();
    navigate(`/app/editor/${project.id}`);
  };

  // ── Shared inner content ───────────────────────────────────────────────────
  // Extracted so it can be placed inside both Dialog and Drawer containers.

  const renderContent = (project: Project) => {
    const title = project.metadata.title || t('projects.untitled');
    const author = project.metadata.author || '';
    const genre = project.metadata.genre || '';
    const progress = deriveProgress(project.chapters);
    const totalWords = project.chapters.reduce((s, c) => s + countWords(c.content), 0);
    const chapterCount = project.chapters.length;
    const lastEdited = formatDate(project.updatedAt);
    // Books shared WITH me (I'm a collaborator, not the owner) only expose the
    // "open to write" action — management belongs to the owner.
    const isOwner = (project as unknown as { isOwner?: boolean }).isOwner !== false;

    return (
      <>
        {/* ── Header with cover ── */}
        <div
          className="relative flex-shrink-0 overflow-hidden"
          style={{
            background:
              'linear-gradient(180deg, var(--color-ink-950) 0%, var(--color-primary-950) 100%)',
            minHeight: 200,
          }}
        >
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60 pointer-events-none" />

          {/* Close button — calls onClose directly (controlled close → animates out) */}
          <button
            onClick={onClose}
            aria-label={t('bookDetail.close', 'Fechar')}
            className={cn(
              'absolute top-4 right-4 z-20 p-2 rounded-full',
              'bg-black/40 hover:bg-black/60 backdrop-blur-sm',
              'text-white border border-white/10',
              'transition-colors',
            )}
          >
            <X className="w-4 h-4" />
          </button>

          {/* Cover + meta */}
          <div className="relative z-10 flex gap-5 p-6 pt-10 md:pt-6 items-end">
            {/*
              Flip-in on mount. Uses rAF to ensure the initial -100 deg state is
              painted before animating to 0, making the flip visible. No layoutId.
            */}
            <motion.div
              animate={{ rotateY: flipped ? 0 : -100 }}
              initial={false}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformPerspective: 1000, transformOrigin: 'left center' }}
              className="w-28 flex-shrink-0 rounded-md shadow-2xl"
            >
              <BookCover
                title={title}
                author={author}
                genre={genre}
                coverImage={project.metadata.coverImage}
                variant="cover"
                progress={progress}
                seed={project.id}
              />
            </motion.div>

            {/* Text metadata */}
            <div className="flex-1 min-w-0 pb-1">
              {genre && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold tracking-widest text-white/70 bg-white/10 border border-white/10 mb-2">
                  {genre}
                </span>
              )}
              <h2 className="font-serif italic font-medium text-white text-xl leading-tight line-clamp-3 mb-1">
                {title}
              </h2>
              {author && (
                <p className="text-white/60 text-xs font-sans tracking-wide">
                  {t('bookDetail.byAuthor', 'por {{author}}', { author })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Scrollable body ── (min-h-0 so the drawer grows to content up to
            max-h, then THIS scrolls — never the whole sheet) */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="p-5 space-y-5">

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  {t('bookDetail.progress', 'Progresso')}
                </span>
                <span className="text-[10px] font-mono text-foreground font-semibold">
                  {progress >= 100 ? (
                    <span className="text-accent flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {t('bookDetail.completed', 'Concluído')}
                    </span>
                  ) : `${progress}%`}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
                />
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                icon={<FileText className="w-3.5 h-3.5" />}
                label={t('bookDetail.chapters', 'Capítulos')}
                value={String(chapterCount)}
              />
              <StatCard
                icon={<BarChart3 className="w-3.5 h-3.5" />}
                label={t('bookDetail.words', 'Palavras')}
                value={totalWords.toLocaleString('pt-BR')}
              />
              <StatCard
                icon={<Clock className="w-3.5 h-3.5" />}
                label={t('bookDetail.edited', 'Editado')}
                value={lastEdited}
                small
              />
            </div>

            {/* Description */}
            {project.metadata.description && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  {t('bookDetail.synopsis', 'Sinopse')}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
                  {project.metadata.description}
                </p>
              </div>
            )}

            {/* Divider */}
            <div className="h-px bg-border" />

            {/* Actions */}
            <div className="space-y-2.5 pb-2">
              {/* Primary CTA */}
              <button
                onClick={handleOpen}
                className={cn(
                  'w-full flex items-center justify-center gap-2',
                  'py-3 px-4 rounded-xl',
                  'bg-primary text-primary-foreground',
                  'font-semibold text-sm',
                  'hover:opacity-90 active:scale-[0.98] transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                )}
              >
                <BookOpen className="w-4 h-4" />
                {t('bookDetail.openInEditor', 'Abrir no Editor')}
              </button>

              {/* Management actions — owner only. Two tidy rows of paired
                  actions, each with an icon + label for clear discoverability. */}
              {isOwner && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <SecondaryAction
                      icon={<Edit className="w-3.5 h-3.5" />}
                      label={t('bookDetail.editDetails', 'Editar Detalhes')}
                      onClick={() => onEditDetails(project)}
                    />
                    <SecondaryAction
                      icon={<ImagePlus className="w-3.5 h-3.5" />}
                      label={t('bookDetail.changeCover', 'Trocar Capa')}
                      onClick={() => onChangeCover(project.id)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SecondaryAction
                      icon={<Copy className="w-3.5 h-3.5" />}
                      label={t('bookDetail.duplicate', 'Duplicar')}
                      onClick={() => { onDuplicate(project.id); onClose(); }}
                    />
                    <SecondaryAction
                      icon={<Share2 className="w-3.5 h-3.5" />}
                      label={t('bookDetail.share', 'Compartilhar')}
                      onClick={() => onShare(project.id)}
                    />
                  </div>

                  {/* Destructive */}
                  <button
                    onClick={() => { onDelete(project.id); onClose(); }}
                    className={cn(
                      'w-full flex items-center justify-center gap-1.5',
                      'py-2.5 px-4 rounded-xl',
                      'text-destructive hover:bg-destructive/10',
                      'text-xs font-medium transition-colors border border-destructive/20',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive',
                    )}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('bookDetail.deleteBook', 'Excluir Livro')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── Mobile: vaul Drawer direction=bottom ────────────────────────────────────
  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
        direction="bottom"
      >
        <DrawerContent
          className={cn(
            'bg-card text-card-foreground border-border',
            'max-h-[90dvh] flex flex-col',
          )}
          aria-label={shownProject?.metadata.title ?? ''}
        >
          {shownProject ? renderContent(shownProject) : null}
        </DrawerContent>
      </Drawer>
    );
  }

  // ── Desktop: Dialog modal centralizado ────────────────────────────────────
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
    >
      <DialogContent
        showCloseButton={false}
        className={cn(
          'bg-card text-card-foreground border-border',
          'sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col',
          'max-h-[90vh]',
        )}
      >
        {shownProject ? renderContent(shownProject) : null}
      </DialogContent>
    </Dialog>
  );
}

// ─── SecondaryAction ──────────────────────────────────────────────────────────

interface SecondaryActionProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function SecondaryAction({ icon, label, onClick }: SecondaryActionProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-1.5',
        'py-2.5 px-3 rounded-xl',
        'bg-muted hover:bg-muted/80 text-foreground',
        'text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  small?: boolean;
}

function StatCard({ icon, label, value, small }: StatCardProps) {
  return (
    <div className="flex flex-col gap-1 p-3 rounded-xl bg-muted/50 border border-border">
      <div className="flex items-center gap-1 text-muted-foreground">
        {icon}
        <span className="text-[9px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <span
        className={cn(
          'font-semibold text-foreground leading-tight',
          small ? 'text-[11px]' : 'text-sm',
        )}
      >
        {value}
      </span>
    </div>
  );
}
